import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Modal } from 'src/components/modals/comps';
import Select, { MultiSelect } from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { showSuccessToast } from 'src/components/Toast';
import { useAuth } from 'src/state/auth';
import {
  UPSERT_CONTENT_PACK_ACCESS,
  UpsertContentPackAccessMutation,
  UpsertContentPackAccessMutationVariables,
} from 'src/graphql/contentImport';

const StyledModal = styled(Modal)`
  width: 760px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface FrameworkOption {
  value: string;
  label: string;
}

interface OrgUnitOption {
  value: string;
  label: string;
}

export interface ControlFrameworkPublishSummaryOption {
  id: string;
  name: string;
  shortname?: string | null;
  controlCount?: number | null;
  featureKey?: string | null;
}

interface PublishControlFrameworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublished: () => Promise<void> | void;
  frameworks: ControlFrameworkPublishSummaryOption[];
}

export const PublishControlFrameworkModal: React.FC<PublishControlFrameworkModalProps> = ({
  isOpen,
  onClose,
  onPublished,
  frameworks,
}) => {
  const { flatOrganisations, workspaceId } = useAuth();

  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState<string[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  const [runUpsertContentPackAccess, { loading: isPublishing }] = useMutation<
    UpsertContentPackAccessMutation,
    UpsertContentPackAccessMutationVariables
  >(UPSERT_CONTENT_PACK_ACCESS);

  const frameworkOptions = useMemo<FrameworkOption[]>(() => {
    return (frameworks || [])
      .map((framework) => ({
        value: framework.id,
        label: framework.shortname || framework.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [frameworks]);

  const organisationOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'O' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const workspaceOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'W' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const selectedFramework = useMemo(
    () => frameworks.find((framework) => framework.id === selectedFrameworkId) || null,
    [frameworks, selectedFrameworkId],
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFrameworkId(frameworks[0]?.id || '');
    setSelectedOrganisationIds([]);
    setSelectedWorkspaceIds(workspaceId ? [workspaceId] : []);
    setError('');
  }, [isOpen, frameworks, workspaceId]);

  const handleClose = () => {
    if (isPublishing) return;
    onClose();
  };

  const hasTargets = selectedOrganisationIds.length > 0 || selectedWorkspaceIds.length > 0;
  const canPublish = !!selectedFramework && hasTargets && !isPublishing;
  const frameworkSelectValue = frameworkOptions.find((option) => option.value === selectedFrameworkId) || null;

  const onPublish = async () => {
    if (!selectedFramework || !hasTargets) return;

    setError('');

    try {
      await runUpsertContentPackAccess({
        variables: {
          packType: 'framework',
          packId: selectedFramework.id,
          organisations: selectedOrganisationIds,
          workspaces: selectedWorkspaceIds,
        },
      });

      await onPublished();
      showSuccessToast({ title: 'Control framework published' });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Control framework publish failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isPublishing}>
      <Content>
        <Poppins className="m-title" px={32}>
          Publish control framework
        </Poppins>

        <Spacer $px={18} />
        <Panel>
          <InputLabel>CONTROL FRAMEWORK</InputLabel>
          <Spacer $px={8} />
          <Select
            options={frameworkOptions}
            value={frameworkSelectValue}
            onChange={(nextValue: unknown) =>
              setSelectedFrameworkId(((nextValue as FrameworkOption | null)?.value || '').trim())
            }
            placeholder="Select control framework"
            isDisabled={isPublishing || !frameworkOptions.length}
          />

          {selectedFramework && (
            <>
              <Spacer $px={16} />
              <SummaryCard>
                <Poppins px={14}>
                  {selectedFramework.shortname || selectedFramework.name} • {selectedFramework.controlCount || 0} controls
                </Poppins>
              </SummaryCard>
            </>
          )}

          <Spacer $px={16} />
          <InputLabel>PUBLISH TO ORGANISATIONS</InputLabel>
          <Spacer $px={8} />
          <MultiSelect
            options={organisationOptions}
            value={selectedOrganisationIds}
            onChange={setSelectedOrganisationIds}
            placeholder="Select organisations"
            selectedLabel="selected"
          />

          <Spacer $px={16} />
          <InputLabel>PUBLISH TO WORKSPACES</InputLabel>
          <Spacer $px={8} />
          <MultiSelect
            options={workspaceOptions}
            value={selectedWorkspaceIds}
            onChange={setSelectedWorkspaceIds}
            placeholder="Select workspaces"
            selectedLabel="selected"
          />

          {!hasTargets && (
            <>
              <Spacer $px={12} />
              <Poppins px={14} color="error">
                Select at least one organisation or workspace to publish this control framework.
              </Poppins>
            </>
          )}
        </Panel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        {!frameworkOptions.length && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              No control frameworks available to publish.
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          <Button primary style={{ minWidth: 300 }} onClick={onPublish} disabled={!canPublish} loading={isPublishing}>
            Publish control framework
          </Button>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
